import { Grid } from '@mui/material'
import { FactCheckOutlined } from '@mui/icons-material'
import { ReportingOverviewCard } from '../../presentation-components/reporting-components/reporting-overview-card'
import hmGovernment from "../../../assets/images/hm-government.png";
import bCorp from "../../../assets/images/bcorp.png";
import ghg from "../../../assets/images/ghg.png";
import gaia from "../../../assets/corporate-logos/gaia/gaia-logo-no-padding.png"
import euFlag from "../../../assets/images/eu-flag.png"
import { useNavigate } from 'react-router-dom';
import React, { useContext, useState } from 'react';
import { BCorpInfoDialog } from '../../dialogs/reporting/b-corp-info-dialog';
import { IBCorpInformation } from '../../../shared/services/generated-report/generated-report.model';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { RegulationApplicabilityCard } from '../../presentation-components/reporting-components/regulation-applicability-card';
import { ReadinessPreflightBanner } from '../../presentation-components/readiness-components/readiness-preflight-banner';

export const ReportingPage = () => {
  const navigate = useNavigate();
  //Context
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [openDialog, setDialogOpen] = React.useState(false);
  //Loading State
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  //Variables
  const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerName : 'Gaia';
  const shouldShowPartnerLogo = (userDetails?.isPartner && userDetails?.isLoggedInAsClient) || (!userDetails?.isPartner && userDetails?.partnerOrganisation);
  const carbonSummaryReportImage = shouldShowPartnerLogo && userDetails?.partnerOrganisation?.partnerIconFilepath ? userDetails.partnerOrganisation.partnerIconFilepath : gaia;
  //Component Event Handlers
  const openBCorpDialogHandler = () => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to view your B-Corp Environment Assessment details.`, buttonCopy: 'Ok', })
    } else {
      setDialogOpen(true);
    }
  };

  const openLinkHandler = (title: string, link: string) => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to view your ${title} details.`, buttonCopy: 'Ok', })
    } else {
      navigate(link);
    }
  }

  const successHandler = (values: IBCorpInformation) => {
    if (!isDialogSubmitting) {
      setIsDialogSubmitting(true)
      navigate(`/reporting/b-corporation?reportPeriodId=${values.reportPeriodId}&totalRevenue=${values.totalRevenue}&renewableEnergyGj=${values.renewableEnergyGj}&emissionOffsetTonnes=${values.emissionOffsetTonnes}`)
    }
  }


  return (
    <PageOuterContainer>
      <PageHeading
        title='Reporting' />
      <ReadinessPreflightBanner />
      <RegulationApplicabilityCard />
      <Grid container spacing={4}>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'Streamlined Energy and Carbon Reporting (SECR)'}
            description={'Generate SECR report for companies within UK regulations.'}
            image={hmGovernment}
            clickHandler={() => openLinkHandler('Streamlined Energy and Carbon Reporting (SECR)', '/reporting/secr')}
            width={'200px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'Regulatory Greenhouse Gas (GHG) Emissions Report'}
            description={'Generate a compliant GHG emissions report aligned with UK regulations.'}
            image={ghg}
            clickHandler={() => openLinkHandler('Regulatory Greenhouse Gas (GHG) Emissions Report', '/reporting/greenhouse-gas-emissions-report')}
            width={'200px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'European Sustainability Reporting Standard (ESRS) E1 - Climate Action'}
            description={'Report climate-related data to meet the requirements of the Corporate Sustainability Reporting Directive (CSRD).'}
            image={euFlag}
            clickHandler={() => openLinkHandler('European Sustainability Reporting Standard (ESRS) E1 - Climate Action', '/reporting/european-sustainability-reporting-standard')}
            width={'200px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'B Corporation Environmental Assessment'}
            description={'Access detailed data and answers needed to complete the environmental section of the B Corp Assessment.'}
            image={bCorp}
            clickHandler={openBCorpDialogHandler}
            width={'50px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={`${companyName} Carbon Summary Report`}
            description={'Generate a summarised report of your emissions profile for your report period.'}
            image={carbonSummaryReportImage}
            clickHandler={() => openLinkHandler(`${companyName} Carbon Summary Report`, '/reporting/carbon-summary-report')}
            width={'150px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'Carbon Reduction Plan (PPN 006)'}
            description={'Generate the Carbon Reduction Plan required to bid for NHS and other UK public-sector contracts, in the Cabinet Office Annex A format. Produces a PDF, a publishable web page and the emissions data behind it.'}
            image={hmGovernment}
            clickHandler={() => openLinkHandler('Carbon Reduction Plan (PPN 006)', '/reporting/carbon-reduction-plan')}
            width={'200px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'Climate Disclosure (UK SRS)'}
            description={'Generate a climate disclosure following the four pillars of UK SRS S2 (IFRS S2): governance, strategy, risk management, and metrics and targets. Produces a PDF and a publishable web page.'}
            image={hmGovernment}
            clickHandler={() => openLinkHandler('Climate Disclosure (UK SRS)', '/reporting/uk-srs')}
            width={'200px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'ESOS Phase 4 (Energy Savings Opportunity Scheme)'}
            description={'Run your ESOS Phase 4 assessment from the energy data already here: qualification, total energy consumption, audits and sign-off, through to the MESOS notification and action plan.'}
            image={hmGovernment}
            clickHandler={() => openLinkHandler('ESOS Phase 4 (Energy Savings Opportunity Scheme)', '/reporting/esos')}
            width={'200px'}
            height={'100px'}
          />
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} sx={{ mb: 3 }}>
          <ReportingOverviewCard
            title={'External Audit Pack'}
            description={'Download an Excel pack for external auditors: full emissions register with data sources and emission factors, methodology, change log and scope totals for each report period.'}
            iconNode={<FactCheckOutlined sx={{ fontSize: 90, color: 'text.primary' }} />}
            clickHandler={() => openLinkHandler('External Audit Pack', '/reporting/audit-pack')}
            width={'150px'}
            height={'100px'}
          />
        </Grid>
      </Grid>
      {openDialog &&
        <BCorpInfoDialog
          isSubmitting={isDialogSubmitting}
          successHandler={successHandler}
          open={openDialog}
          closeHandler={() => { setDialogOpen(false) }} />}
    </PageOuterContainer>
  )
}